import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Star, Search, Check, X, Trash2, Loader2, MessageSquare, Filter } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const EASE = [0.22, 1, 0.36, 1];

const STATUS_FILTERS = [
  { key: "pending",  label: "Pending",  tone: "amber" },
  { key: "approved", label: "Approved", tone: "lime" },
  { key: "rejected", label: "Rejected", tone: "red" },
  { key: "all",      label: "All",      tone: "slate" }
];

export default function AdminReviews() {
  const [reviews, setReviews] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const url = filter === "all" ? `${API}/admin/reviews` : `${API}/admin/reviews?status=${filter}`;
      const r = await axios.get(url, { withCredentials: true });
      setReviews(r.data.reviews || []);
      setCounts(r.data.counts || {});
    } catch {
      toast.error("Failed to load reviews");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filter]);

  const filtered = useMemo(() => {
    if (!search.trim()) return reviews;
    const q = search.toLowerCase();
    return reviews.filter(r =>
      (r.user_name || "").toLowerCase().includes(q) ||
      (r.comment || "").toLowerCase().includes(q) ||
      (r.product_name_en || "").toLowerCase().includes(q) ||
      (r.product_name_id || "").toLowerCase().includes(q)
    );
  }, [reviews, search]);

  const moderate = async (reviewId, status) => {
    setBusy(prev => ({ ...prev, [reviewId]: true }));
    try {
      await axios.put(`${API}/admin/reviews/${reviewId}/status`, { status }, { withCredentials: true });
      toast.success(`Marked as ${status}`);
      await load();
    } catch {
      toast.error("Failed to update");
    } finally {
      setBusy(prev => ({ ...prev, [reviewId]: false }));
    }
  };

  const remove = async (reviewId) => {
    if (!window.confirm("Delete this review permanently?")) return;
    setBusy(prev => ({ ...prev, [reviewId]: true }));
    try {
      await axios.delete(`${API}/admin/reviews/${reviewId}`, { withCredentials: true });
      toast.success("Deleted");
      await load();
    } catch {
      toast.error("Failed to delete");
    } finally {
      setBusy(prev => ({ ...prev, [reviewId]: false }));
    }
  };

  return (
    <div data-testid="admin-reviews-page">
      <header className="mb-10">
        <div className="flex items-center gap-3 mb-4">
          <span className="w-8 h-px bg-seikat-pink" />
          <span className="eyebrow-immersive">Reviews</span>
        </div>
        <h1 className="font-heading text-4xl lg:text-5xl text-seikat-bone leading-tight">
          Moderasi <em className="text-seikat-pink">Ulasan</em>.
        </h1>
        <p className="font-body text-seikat-bone/65 mt-3 max-w-2xl">
          Setiap ulasan masuk ke antrian <strong>Pending</strong> dan baru tayang di halaman produk setelah kamu setujui.
        </p>
      </header>

      {/* Counts row */}
      <div className="grid grid-cols-3 gap-4 mb-8" data-testid="reviews-counts">
        {[
          { key: "pending", label: "Pending", value: counts.pending || 0, color: "text-amber-500" },
          { key: "approved", label: "Approved", value: counts.approved || 0, color: "text-seikat-lime" },
          { key: "rejected", label: "Rejected", value: counts.rejected || 0, color: "text-red-500" }
        ].map(s => (
          <button
            key={s.key}
            onClick={() => setFilter(s.key)}
            className={`glass-dark rounded-2xl p-5 text-left transition-all hover:scale-[1.02] ${filter === s.key ? "ring-2 ring-seikat-pink" : ""}`}
            data-testid={`reviews-count-${s.key}`}
          >
            <div className={`font-heading text-4xl ${s.color} leading-none`}>{s.value}</div>
            <div className="font-body text-[10px] tracking-widest uppercase text-seikat-bone-muted mt-2">{s.label}</div>
          </button>
        ))}
      </div>

      {/* Filter tabs + Search */}
      <div className="flex flex-col sm:flex-row gap-3 mb-8">
        <div className="flex p-1 rounded-full bg-white/55 border border-seikat-pink/15 backdrop-blur" data-testid="reviews-filter-tabs">
          {STATUS_FILTERS.map(t => (
            <button
              key={t.key}
              onClick={() => setFilter(t.key)}
              className={`px-4 py-2 rounded-full text-[10px] tracking-[0.25em] uppercase transition-all ${filter === t.key ? "bg-seikat-pink text-white" : "text-seikat-bone-muted hover:text-seikat-bone"}`}
              data-testid={`reviews-tab-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex-1 flex items-center gap-3 bg-white/60 border border-seikat-pink/15 rounded-full px-4 py-2.5">
          <Search size={14} className="text-seikat-bone-muted" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by user, product, or comment..."
            className="flex-1 bg-transparent outline-none text-sm font-body text-seikat-bone placeholder:text-seikat-bone-muted/60"
            data-testid="reviews-search"
          />
        </div>
      </div>

      {/* Reviews list */}
      {loading ? (
        <div className="flex justify-center py-20"><Loader2 size={24} className="animate-spin text-seikat-pink" /></div>
      ) : filtered.length === 0 ? (
        <div className="glass-dark rounded-3xl p-16 text-center" data-testid="reviews-empty">
          <MessageSquare size={32} className="text-seikat-pink/60 mx-auto mb-4" strokeWidth={1.4} />
          <p className="font-heading text-2xl text-seikat-bone">No {filter !== "all" ? filter : ""} reviews</p>
          <p className="font-body text-seikat-bone/55 text-sm mt-2">
            {filter === "pending" ? "Antrian moderasi kosong. Kerja bagus!" : "Belum ada ulasan di kategori ini."}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          <AnimatePresence>
            {filtered.map((r, i) => (
              <motion.div
                key={r.review_id}
                layout
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.97 }}
                transition={{ duration: 0.4, delay: (i % 6) * 0.03, ease: EASE }}
                className="glass-dark rounded-2xl p-5 lg:p-6"
                data-testid={`review-${r.review_id}`}
              >
                <div className="flex flex-col lg:flex-row gap-5">
                  {/* Product thumb */}
                  {r.product_image && (
                    <img
                      src={r.product_image.startsWith("http") ? r.product_image : `${BACKEND}${r.product_image}`}
                      alt=""
                      className="w-full lg:w-20 lg:h-20 h-32 object-cover rounded-xl flex-shrink-0"
                    />
                  )}
                  {/* Content */}
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-3 mb-2">
                      <span className="font-heading text-lg text-seikat-bone">{r.user_name || "Anonymous"}</span>
                      <div className="flex gap-0.5">
                        {Array.from({ length: 5 }).map((_, idx) => (
                          <Star key={idx} size={12} fill={idx < r.rating ? "#D4859B" : "none"} className={idx < r.rating ? "text-seikat-pink" : "text-seikat-bone-muted/30"} />
                        ))}
                      </div>
                      <StatusBadge status={r.status} />
                      <span className="text-[10px] tracking-widest uppercase text-seikat-bone-muted">
                        {new Date(r.created_at).toLocaleDateString("id-ID", { day: "numeric", month: "short", year: "numeric" })}
                      </span>
                    </div>
                    {(r.product_name_id || r.product_name_en) && (
                      <p className="text-xs text-seikat-bone-muted mb-2">
                        <span className="text-seikat-pink">{r.product_name_id || r.product_name_en}</span>
                      </p>
                    )}
                    {r.comment ? (
                      <p className="font-body text-sm text-seikat-bone/80 leading-relaxed">
                        “{r.comment}”
                      </p>
                    ) : (
                      <p className="font-body text-xs text-seikat-bone-muted italic">No written comment.</p>
                    )}
                  </div>
                  {/* Actions */}
                  <div className="flex lg:flex-col gap-2 lg:items-stretch">
                    {r.status !== "approved" && (
                      <button
                        onClick={() => moderate(r.review_id, "approved")}
                        disabled={busy[r.review_id]}
                        className="btn-pill text-[10px] px-4 py-2 disabled:opacity-50"
                        style={{ background: "#C5DB7B", color: "#1C1A19", borderColor: "#C5DB7B" }}
                        data-testid={`review-approve-${r.review_id}`}
                      >
                        <Check size={11} /> Approve
                      </button>
                    )}
                    {r.status !== "rejected" && (
                      <button
                        onClick={() => moderate(r.review_id, "rejected")}
                        disabled={busy[r.review_id]}
                        className="btn-pill text-[10px] px-4 py-2 disabled:opacity-50"
                        data-testid={`review-reject-${r.review_id}`}
                      >
                        <X size={11} /> Reject
                      </button>
                    )}
                    <button
                      onClick={() => remove(r.review_id)}
                      disabled={busy[r.review_id]}
                      className="btn-pill text-[10px] px-4 py-2 disabled:opacity-50 hover:!text-red-500"
                      data-testid={`review-delete-${r.review_id}`}
                    >
                      <Trash2 size={11} /> Delete
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending: { bg: "bg-amber-100", text: "text-amber-700", label: "Pending" },
    approved: { bg: "bg-seikat-lime/30", text: "text-seikat-lime", label: "Approved" },
    published: { bg: "bg-seikat-lime/30", text: "text-seikat-lime", label: "Approved" },
    rejected: { bg: "bg-red-100", text: "text-red-700", label: "Rejected" }
  };
  const t = map[status] || { bg: "bg-white/30", text: "text-seikat-bone-muted", label: status };
  return <span className={`text-[9px] tracking-widest uppercase px-2 py-0.5 rounded-full ${t.bg} ${t.text}`}>{t.label}</span>;
}
